import { Outlet, useNavigate, useLocation } from "react-router-dom";
import { useEffect } from "react";
import { Crown, LogOut } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { tokenValid } from "@/lib/api";
import AdminLogin from "@/pages/AdminLogin";
import { PasskeyPrompt } from "@/components/PasskeyPrompt";
import { ADMIN_SECTIONS } from "@/components/admin/AdminBottomNav";

// Standalone admin area: no store AppShell. Without a valid platform-admin session every /admin route shows the admin login.
export const AdminGate = () => {
  const { user } = useAuth();
  const ok = user?.is_platform_admin && tokenValid(localStorage.getItem("zc_store_token"));
  useEffect(() => {
    const root = document.documentElement;
    const prev = [root.dir, root.lang];
    root.dir = "ltr"; root.lang = "en";
    return () => { [root.dir, root.lang] = prev; };
  }, []);
  return ok ? <AdminLayout /> : <AdminLogin />;
};

const AdminSidebar = ({ onLogout }) => {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const seg = pathname.split("/")[2] || "";
  const current = seg === "store" ? "stores" : seg;
  const cls = (on) => `w-full flex items-center gap-3 px-3 h-10 rounded-xl text-sm font-semibold transition-colors ${on ? "bg-[#6B21A8]/10 text-[#6B21A8]" : "text-slate-600 hover:bg-slate-50 hover:text-slate-900"}`;
  return (
    <aside data-testid="admin-sidebar" className="hidden lg:flex fixed inset-y-0 left-0 z-40 w-64 flex-col bg-white border-r border-slate-200">
      <button data-testid="admin-side-home" onClick={() => navigate("/admin/stores")} className="h-16 px-5 flex items-center gap-2.5 border-b border-slate-100 font-extrabold font-head text-left text-[#6B21A8]">
        <Crown className="w-5 h-5 text-[#D97706]" /> ZapBasket Admin
      </button>
      <nav className="flex-1 overflow-y-auto p-3 space-y-1">
        {ADMIN_SECTIONS.map(([v, l, Icon]) => (
          <button key={v} data-testid={`admin-side-${v}`} onClick={() => navigate(`/admin/${v}`)} className={cls(current === v)}>
            <Icon className="w-[18px] h-[18px]" />{l}
          </button>
        ))}
      </nav>
      <div className="p-3 border-t border-slate-100">
        <button data-testid="admin-side-logout" onClick={onLogout} className="w-full flex items-center gap-3 px-3 h-10 rounded-xl text-sm font-semibold text-slate-600 hover:bg-red-50 hover:text-red-600 transition-colors"><LogOut className="w-[18px] h-[18px]" /> Log out</button>
      </div>
    </aside>
  );
};

const AdminLayout = () => {
  const { logout } = useAuth();
  const navigate = useNavigate();
  const out = () => { logout(); navigate("/admin/stores", { replace: true }); };
  return (
    <div className="min-h-screen bg-[#F8FAFC] lg:pl-64" data-testid="admin-layout">
      <AdminSidebar onLogout={out} />
      <header className="lg:hidden sticky top-0 z-30 bg-[#6B21A8] text-white shadow-md">
        <div className="max-w-3xl mx-auto px-4 h-14 flex items-center justify-between">
          <button data-testid="admin-home" onClick={() => navigate("/admin/stores")} className="flex items-center gap-2 font-extrabold font-head"><Crown className="w-5 h-5 text-[#FBBF24]" /> ZapBasket Admin</button>
          <button data-testid="admin-logout" onClick={out} className="flex items-center gap-1.5 text-xs font-bold text-white/90 hover:text-white"><LogOut className="w-4 h-4" /> Log out</button>
        </div>
      </header>
      <main className="max-w-3xl lg:max-w-[1600px] mx-auto px-4 lg:px-8 py-5 lg:py-8"><Outlet /></main>
      <PasskeyPrompt accent="bg-[#6B21A8] hover:bg-[#581C87]" />
    </div>
  );
};
